import { Link, Typography } from '@mui/material';
import { ToastType } from 'services/toast';

type WorkflowPushResult =
  | {
      queued?: number | null;
      summary?: { alreadyEnrolled?: number | null } | null;
    }
  | null
  | undefined;

type DescribeWorkflowPushResultProps = {
  result: WorkflowPushResult;
  /** How many candidates were sent, so a shortfall can be reported honestly. */
  sent: number;
  workflowLabel: string;
  workflowUrl: string;
};

type DescribedPush = {
  didEnrol: boolean;
  toast: {
    title: string;
    message?: string | React.ReactElement;
    type: ToastType;
  };
};

/**
 * Turns a push result into what the recruiter is told. Shared by the drawer and the dialog so
 * the two can't drift on what counts as success.
 *
 * The shortfall is `sent - queued` rather than any single field: a candidate can be missing
 * because the workflow rejected them, because someone enrolled them between the check and the
 * push, or for a reason not yet enumerated. Counting the gap means a new one can't read as
 * success.
 */
export const describeWorkflowPushResult = ({
  result,
  sent,
  workflowLabel,
  workflowUrl
}: DescribeWorkflowPushResultProps): DescribedPush => {
  const queued = result?.queued ?? 0;
  const skipped = Math.max(sent - queued, 0);
  const alreadyEnrolled = result?.summary?.alreadyEnrolled ?? 0;

  if (queued === 0) {
    return {
      didEnrol: false,
      toast: {
        title: 'Could not add these candidates to the workflow',
        message:
          alreadyEnrolled >= sent
            ? 'They are all already in this workflow.'
            : 'Nobody was added. Try again in a moment.',
        type: ToastType.Error
      }
    };
  }

  if (skipped > 0) {
    return {
      didEnrol: true,
      toast: {
        title: `Added ${queued} of ${sent} candidates`,
        message:
          alreadyEnrolled >= skipped
            ? `${skipped} ${
                skipped === 1 ? 'was' : 'were'
              } already in this workflow.`
            : `${skipped} could not be added.`,
        type: ToastType.Warning
      }
    };
  }

  return {
    didEnrol: true,
    toast: {
      title:
        queued === 1
          ? 'Candidate added to workflow'
          : 'Candidates added to workflow',
      // Typography, not a bare fragment: Toast renders an element message without a wrapper,
      // so a fragment runs straight on from the title — "…added to workflowView Retail triage".
      // The sibling TalentPoolCreationToastMessage wraps itself for the same reason.
      message: (
        <Typography variant="body2" mt={1}>
          View{' '}
          <Link href={workflowUrl} target="_blank" rel="noopener">
            {workflowLabel}
          </Link>
        </Typography>
      ),
      type: ToastType.Success
    }
  };
};
